import React from 'react';
import PropTypes from 'prop-types';
import { GECKO_CONSTS, GeckoField, GeckoBtn } from 'ui';

import './index.scss';
import ReorderArrowControls from '../ReorderArrowControls';
import { changeSingleValue, moveItemInArray } from './utils';

const valuesWithIds = (values) => {
  return values.map((val, idx) => ({
    value: val,
    id: idx,
  }));
};

const BlockField = (props) => {
  const {
    onChange,
    value,
    defaultValue,
    maxItems,
    isAddButtonDisabled,
  } = props;

  const values = valuesWithIds(props.value);

  const updateValues = (newValues) => {
    const valuesWithoutIds = newValues.map((val) => val.value);
    onChange(valuesWithoutIds);
  };

  const addBlock = () => {
    const newValues = [...value, defaultValue];
    onChange(newValues);
  };

  const removeBlock = (blockIndex) => {
    const newValues = values.filter((val) => val.id !== blockIndex);
    updateValues(newValues);
  };

  const handleChange = (idx, newValue) => {
    const newValues = changeSingleValue(idx, newValue, values);
    updateValues(newValues);
  };

  const moveItem = (item, newIndex) => {
    const reorderedItems = moveItemInArray(item, newIndex, values);
    updateValues(reorderedItems);
  };

  const addDisabled = () => {
    const itemsExceedMax = values.length >= maxItems;

    return itemsExceedMax || isAddButtonDisabled;
  };

  const getRemoveButton = (removeButton, idx) => {
    return <GeckoBtn {...removeButton} handler={() => removeBlock(idx)} />;
  };

  const getReorderControls = (idx, val) => {
    const isFirstItem = idx === 0;
    const isLastItem = idx === values.length - 1;

    return (
      <ReorderArrowControls
        onUp={() => moveItem(val, idx - 1)}
        onDown={() => moveItem(val, idx + 1)}
        upDisabled={isFirstItem}
        downDisabled={isLastItem}
      />
    );
  };

  const getField = ({ id, value }, field, idx) => {
    return (
      <GeckoField
        values={{ [id]: value }}
        field={field}
        handler={(_, newValue) => handleChange(idx, newValue)}
      />
    );
  };

  const renderValues = () => {
    const { isReorderable, removeButton, stacked } = props;

    return values.map((val, idx) => {
      const field = { ...props.field, ...{ key: idx.toString() } };

      const fieldJsx = getField(val, field, idx);
      const reorderControlsJsx = getReorderControls(idx, val);
      const removeButtonJsx = getRemoveButton(removeButton, idx);

      if (isReorderable && stacked) {
        return (
          <div
            className={`BlockField-Block ${
              !field.label && 'Blockfield-Block-no-label'
            } reorderable-stacked`}
            key={val.id}
          >
            {fieldJsx}
            <div className="control">{reorderControlsJsx}</div>
            {removeButtonJsx}
          </div>
        );
      }

      if (isReorderable) {
        return (
          <div
            className={`BlockField-Block ${
              !field.label && 'Blockfield-Block-no-label'
            } reorderable`}
            key={val.id}
          >
            <div className="field">{fieldJsx}</div>
            {isReorderable && (
              <div className="control">{reorderControlsJsx}</div>
            )}
            <div className="control">{removeButtonJsx}</div>
          </div>
        );
      }

      return (
        <div
          className={`BlockField-Block ${
            !field.label && 'Blockfield-Block-no-label'
          } default`}
          key={val.id}
        >
          {fieldJsx}
          {removeButtonJsx}
        </div>
      );
    });
  };

  return (
    <div className="BlockField">
      {renderValues()}
      <GeckoBtn
        disabledWhen={addDisabled}
        handler={addBlock}
        {...props.addButton}
      />
    </div>
  );
};

BlockField.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  addButton: PropTypes.shape({
    preset: PropTypes.string,
  }),
  removeButton: PropTypes.shape({
    preset: PropTypes.string,
  }),
  field: PropTypes.shape({
    type: PropTypes.string,
  }),
  defaultValue: PropTypes.string,
  maxItems: PropTypes.number,
  isReorderable: PropTypes.bool,
  stacked: PropTypes.bool,
  isAddButtonDisabled: PropTypes.bool,
};

BlockField.defaultProps = {
  value: [{}],
  onChange: () => {},
  addButton: {
    preset: 'add',
  },
  removeButton: {
    preset: 'remove',
  },
  field: {
    type: GECKO_CONSTS.FIELD_TYPE_TEXT,
  },
  defaultValue: '',
  maxItems: Infinity,
  isReorderable: false,
  stacked: false,
  isAddButtonDisabled: false,
};

export default BlockField;
